
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  Zap, 
  Target, 
  Award,
  Star,
  Crown,
  Trophy,
  Flame,
  Users
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

/**
 * Centse Score Card Component
 * 
 * A modern, gamified profile card showing the user's financial literacy rating.
 * Features:
 * - Dynamic score calculation based on learning progress
 * - Animated progress bars and score display
 * - Level-based ranking system with badges
 * - Friend network influence on score
 * - Glassmorphism design with gradients
 * - Micro-interactions and hover effects
 * 
 * The Centse Score is calculated based on:
 * - Lessons completed (35% weight)
 * - Learning streak (20% weight) 
 * - Quiz performance (25% weight)
 * - Community engagement (10% weight)
 * - Friend network quality (10% weight) - NEW!
 */
const CentseScoreCard = () => {
  const [animatedScore, setAnimatedScore] = useState(0);

  // Fetch user progress data for score calculation
  const { data: scoreData } = useQuery({
    queryKey: ['centseScore'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      console.log("🎯 Calculating Centse Score for user:", user.id);

      // Get user progress for lessons completed
      const { data: userProgress } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      // Get user points
      const { data: userPoints } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      // Get total lessons for completion percentage
      const { data: totalLessons } = await supabase
        .from('lessons')
        .select('id')
        .eq('status', 'active');

      // Get friends data for friend network score
      const { data: friendsData } = await supabase
        .from('leaderboard_view')
        .select('*')
        .eq('is_friend', true);

      console.log("👥 Found friends data:", friendsData?.length || 0, "friends");

      const completedLessons = userProgress?.filter(p => p.status === 'completed').length || 0;
      const totalLessonCount = totalLessons?.length || 1;
      const completionRate = (completedLessons / totalLessonCount) * 100;

      // Calculate learning streak (simplified - consecutive days)
      const learningStreak = Math.min(
        userProgress?.filter(p => p.status === 'completed').length || 0, 
        30
      ); // Cap at 30 days

      // Get quiz performance (using points as proxy)
      const totalPoints = userPoints?.total_points || 0;
      const quizPerformance = Math.min((totalPoints / 1000) * 100, 100); // Normalize to 100

      // Calculate Friend Network Score - NEW FEATURE!
      let friendNetworkScore = 0;
      if (friendsData && friendsData.length > 0) {
        // Average friend score (points normalized to 100 scale)
        const friendsAveragePoints = friendsData.reduce((sum, friend) => sum + (friend.points || 0), 0) / friendsData.length;
        const friendsScoreNormalized = Math.min((friendsAveragePoints / 500) * 100, 100); // Normalize to 100
        
        // Bonus for having multiple financially literate friends
        const friendCountBonus = Math.min(friendsData.length * 5, 25); // Up to 25 points for having 5+ friends
        
        friendNetworkScore = (friendsScoreNormalized * 0.7) + (friendCountBonus * 0.3);
        
        console.log("🤝 Friend network analysis:", {
          friendsCount: friendsData.length,
          friendsAveragePoints,
          friendsScoreNormalized,
          friendCountBonus,
          finalFriendNetworkScore: friendNetworkScore
        });
      }

      // Calculate Centse Score (0-1000 scale) - Updated weights to include friend network
      const centseScore = Math.round(
        (completionRate * 0.35) +           // 35% lessons completed (reduced from 40%)
        (learningStreak * 0.20) +          // 20% learning streak  
        (quizPerformance * 0.25) +         // 25% quiz performance (reduced from 30%)
        (Math.min(completedLessons * 2, 100) * 0.10) + // 10% community engagement
        (friendNetworkScore * 0.10)       // 10% friend network quality - NEW!
      ) * 10; // Scale to 1000

      console.log("📊 Score breakdown:", {
        completionRate,
        learningStreak, 
        quizPerformance,
        friendNetworkScore,
        centseScore
      });

      return {
        score: Math.min(centseScore, 1000),
        completedLessons,
        totalLessons: totalLessonCount,
        learningStreak,
        totalPoints,
        completionRate,
        friendNetworkScore,
        friendsCount: friendsData?.length || 0
      };
    }
  });

  // Animate score on load
  useEffect(() => {
    if (scoreData?.score) {
      const timer = setTimeout(() => {
        let current = 0;
        const increment = scoreData.score / 30; // Animate over 30 frames
        const animate = () => {
          current += increment;
          if (current < scoreData.score) {
            setAnimatedScore(Math.round(current));
            requestAnimationFrame(animate);
          } else {
            setAnimatedScore(scoreData.score);
          }
        };
        animate();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [scoreData?.score]);

  // Determine level and badge based on score
  const getLevel = (score: number) => {
    if (score >= 900) return { level: "Financial Guru", icon: Crown, color: "from-yellow-400 to-orange-500", bgColor: "bg-gradient-to-r from-yellow-100 to-orange-100" };
    if (score >= 750) return { level: "Money Master", icon: Trophy, color: "from-purple-400 to-pink-500", bgColor: "bg-gradient-to-r from-purple-100 to-pink-100" };
    if (score >= 600) return { level: "Finance Pro", icon: Award, color: "from-blue-400 to-cyan-500", bgColor: "bg-gradient-to-r from-blue-100 to-cyan-100" };
    if (score >= 400) return { level: "Money Smart", icon: Star, color: "from-green-400 to-emerald-500", bgColor: "bg-gradient-to-r from-green-100 to-emerald-100" };
    if (score >= 200) return { level: "Learning", icon: Zap, color: "from-indigo-400 to-blue-500", bgColor: "bg-gradient-to-r from-indigo-100 to-blue-100" };
    return { level: "Beginner", icon: Target, color: "from-gray-400 to-gray-500", bgColor: "bg-gradient-to-r from-gray-100 to-gray-200" };
  };

  const score = scoreData?.score || 0;
  const level = getLevel(score);
  const LevelIcon = level.icon;
  const progressToNext = ((score % 200) / 200) * 100; // Progress to next level tier

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 group">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/5 to-pink-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" />
      
      <CardContent className="relative p-8 space-y-6">
        {/* Header with score and level badge */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-gray-800">Your Centse Score</h3>
            <p className="text-sm text-gray-600">Financial literacy rating</p>
          </div>
          
          <Badge className={`${level.bgColor} text-gray-700 border-0 px-3 py-1 font-semibold shadow-sm`}>
            <LevelIcon className="h-3 w-3 mr-1" />
            {level.level}
          </Badge>
        </div>

        {/* Main score display with animation */}
        <div className="text-center space-y-4">
          <div className="relative">
            <div className={`text-6xl font-black bg-gradient-to-r ${level.color} bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
              {animatedScore}
            </div>
            <div className="text-sm text-gray-500 font-medium">out of 1000</div>
          </div>

          {/* Progress ring or bar to next level */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Progress to next level</span>
              <span>{Math.round(progressToNext)}%</span>
            </div>
            <Progress 
              value={progressToNext} 
              className="h-2 bg-gray-200"
            />
          </div>
        </div>

        {/* Stats breakdown - Updated to include friend network */}
        <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-200/50">
          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-green-100 rounded-lg w-fit mx-auto group-hover/stat:bg-green-200 transition-colors">
              <Target className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {scoreData?.completedLessons || 0}
            </div>
            <div className="text-xs text-gray-500">Lessons</div>
          </div>

          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-orange-100 rounded-lg w-fit mx-auto group-hover/stat:bg-orange-200 transition-colors">
              <Flame className="h-4 w-4 text-orange-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {scoreData?.learningStreak || 0}
            </div>
            <div className="text-xs text-gray-500">Day Streak</div>
          </div>

          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-blue-100 rounded-lg w-fit mx-auto group-hover/stat:bg-blue-200 transition-colors">
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {Math.round(scoreData?.completionRate || 0)}%
            </div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>

          <div className="text-center space-y-1 group/stat hover:scale-105 transition-transform duration-200">
            <div className="p-2 bg-purple-100 rounded-lg w-fit mx-auto group-hover/stat:bg-purple-200 transition-colors">
              <Users className="h-4 w-4 text-purple-600" />
            </div>
            <div className="text-sm font-semibold text-gray-800">
              {scoreData?.friendsCount || 0}
            </div>
            <div className="text-xs text-gray-500">Smart Friends</div>
          </div>
        </div>

        {/* Motivational message based on score - Updated to include friend network tips */}
        <div className="text-center pt-2">
          {score >= 750 && (
            <p className="text-sm text-gray-600 font-medium">
              🔥 You're crushing it! Your friend network is boosting your score!
            </p>
          )}
          {score >= 400 && score < 750 && (
            <p className="text-sm text-gray-600 font-medium">
              💪 Nice progress! {scoreData?.friendsCount ? "Your smart friends are helping!" : "Add some financially savvy friends to boost your score!"}
            </p>
          )}
          {score < 400 && (
            <p className="text-sm text-gray-600 font-medium">
              🚀 Just getting started? {scoreData?.friendsCount ? "Keep learning with your friends!" : "Connect with friends to learn together!"}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CentseScoreCard;
